import React, { useEffect, useState, useMemo } from 'react';
import './AvatarRevealLoader.css';
import femaleSilhouette from '../../../assets/placeholders/female-silhouette.jpg';
import maleSilhouette from '../../../assets/placeholders/male-silhouette.jpg';

export interface AvatarRevealLoaderProps {
    isComplete?: boolean;
    avatarUrl?: string;
    placeholderImage?: string;
    duration?: number;
    selfieUrl?: string;
    gender?: 'male' | 'female';
}

const MESSAGE_TIMELINE = [
    { time: 0, msg: "Yüz hatları ve cilt tonu analiz ediliyor..." },
    { time: 4000, msg: "Yüz geometrisi haritalanıyor ve 3D model oluşturuluyor..." },
    { time: 8000, msg: "Mimikler ve detaylar işleniyor, dijital ikiziniz hazırlanıyor..." },
    { time: 15000, msg: "AI modeliniz son rötuşlarla tamamlanıyor..." },
    { time: 22000, msg: "Neredeyse hazır, son kontroller yapılıyor..." }
];

const BLUR_STEPS = [
    { time: 0, blur: 50 },
    { time: 2000, blur: 45 },
    { time: 4000, blur: 40 },
    { time: 6000, blur: 35 },
    { time: 8000, blur: 30 },
    { time: 10000, blur: 25 },
    { time: 12000, blur: 20 },
    { time: 14000, blur: 16 },
    { time: 16000, blur: 12 },
    { time: 18000, blur: 9 },
    { time: 20000, blur: 7 },
    { time: 22000, blur: 5 },
    { time: 24000, blur: 4 },
    { time: 26000, blur: 3 },
    { time: 28000, blur: 2 },
];

const PARTICLE_COUNT = 12;

export const AvatarRevealLoader: React.FC<AvatarRevealLoaderProps> = ({
    isComplete = false,
    avatarUrl,
    placeholderImage,
    duration = 30000,
    selfieUrl,
    gender = 'female'
}) => {
    const [message, setMessage] = useState(MESSAGE_TIMELINE[0].msg);
    const [progress, setProgress] = useState(0);
    const [blurAmount, setBlurAmount] = useState(BLUR_STEPS[0].blur);

    const defaultPlaceholder = gender === 'female' ? femaleSilhouette : maleSilhouette;
    const displayImage = isComplete && avatarUrl ? avatarUrl : (placeholderImage || defaultPlaceholder);

    const particles = useMemo(() => {
        return Array.from({ length: PARTICLE_COUNT }, (_, i) => ({
            id: i,
            left: Math.random() * 100,
            top: Math.random() * 100,
            size: 2 + Math.random() * 4,
            delay: Math.random() * 3,
            duration: 2 + Math.random() * 2
        }));
    }, []);

    useEffect(() => {
        if (isComplete) {
            setBlurAmount(0);
            setProgress(100);
            return;
        }

        const startTime = Date.now();

        const updateProgress = () => {
            const elapsed = Date.now() - startTime;
            const progressPercent = Math.min((elapsed / duration) * 100, 95);
            setProgress(progressPercent);

            if (elapsed < duration && !isComplete) {
                requestAnimationFrame(updateProgress);
            }
        };

        const frameId = requestAnimationFrame(updateProgress);

        return () => cancelAnimationFrame(frameId);
    }, [duration, isComplete]);

    useEffect(() => {
        if (isComplete) {
            setBlurAmount(0);
            return;
        }

        const timeouts: NodeJS.Timeout[] = [];

        BLUR_STEPS.forEach(({ time, blur }) => {
            const timeout = setTimeout(() => {
                setBlurAmount(blur);
            }, time);
            timeouts.push(timeout);
        });

        return () => {
            timeouts.forEach(clearTimeout);
        };
    }, [isComplete]);

    useEffect(() => {
        if (isComplete) return;

        const timeouts: NodeJS.Timeout[] = [];

        MESSAGE_TIMELINE.forEach(({ time, msg }) => {
            if (time > 0) {
                const timeout = setTimeout(() => {
                    setMessage(msg);
                }, time);
                timeouts.push(timeout);
            }
        });

        return () => {
            timeouts.forEach(clearTimeout);
        };
    }, [isComplete]);

    return (
        <div className="avatar-reveal-container">
            <div className="avatar-reveal-content">
                <div className={`avatar-reveal-frame ${isComplete ? 'completed' : ''}`}>
                    <div className="avatar-image-wrapper">
                        <img
                            src={displayImage}
                            alt="Avatar"
                            className="avatar-image"
                            style={{ filter: `blur(${blurAmount}px)` }}
                        />

                        {!isComplete && (
                            <>
                                <div className="scanning-beam" />
                                <div className="scanning-beam secondary" />

                                <div className="edge-glow top" />
                                <div className="edge-glow right" />
                                <div className="edge-glow bottom" />
                                <div className="edge-glow left" />

                                <div className="particle-container">
                                    {particles.map((particle) => (
                                        <div
                                            key={particle.id}
                                            className="ai-particle"
                                            style={{
                                                left: `${particle.left}%`,
                                                top: `${particle.top}%`,
                                                width: `${particle.size}px`,
                                                height: `${particle.size}px`,
                                                animationDelay: `${particle.delay}s`,
                                                animationDuration: `${particle.duration}s`
                                            }}
                                        />
                                    ))}
                                </div>

                                <div className="corner-accent top-left" />
                                <div className="corner-accent top-right" />
                                <div className="corner-accent bottom-left" />
                                <div className="corner-accent bottom-right" />

                                <div className="ai-grid-overlay" />
                            </>
                        )}

                        {isComplete && (
                            <div className="completion-flash" />
                        )}

                        {selfieUrl && (
                            <div className="selfie-pip-container">
                                <img
                                    src={selfieUrl}
                                    alt="Your Selfie"
                                    className="selfie-pip-image"
                                />
                            </div>
                        )}
                    </div>

                    <div className="progress-container">
                        <div 
                            className="progress-fill"
                            style={{ width: `${progress}%` }}
                        />
                        <div className="progress-glow" style={{ left: `${progress}%` }} />
                    </div>
                </div>

                <div className="message-container">
                    <div className="ai-badge">
                        <span className="ai-icon">✦</span>
                        <span>Avatar Oluşturuluyor</span>
                    </div>
                    <p className={`status-message ${isComplete ? 'completed' : ''}`}>
                        {isComplete ? "Avatar başarıyla oluşturuldu!" : message}
                    </p>
                    {!isComplete && (
                        <div className="processing-dots">
                            <span className="dot" />
                            <span className="dot" />
                            <span className="dot" />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AvatarRevealLoader;
